import { isCompleted, isDisrupted, isLive as isLiveStatus, isPreMatch } from '@/constants/matchStatus';
import i18n from '@/i18n';

// Stage-aware matchday for grouping matches by stage + matchday
export interface StageMatchday {
  id: string; // "Regular Season - 28", "Quarter-finals", "EXTRA"
  stage: string;
  matchday: number;
  label: string;
  matches_count: number;
  finished_count: number;
  upcoming_count: number;
  earliest_date: string;
  latest_date: string;
  /**
   * True when EVERY match in this stage's group is a frontend-injected
   * synthetic placeholder (e.g. World Cup 2026 knockout-bracket template
   * before the API publishes real fixtures). `detectInitialMatchday` filters
   * these out so synthetic stages never compete for the auto-selected round.
   * Once the API publishes at least one real fixture for the stage, the flag
   * flips back to false/undefined and the stage participates normally.
   */
  isSynthetic?: boolean;
}

// Minimal match interface for matchday helpers
export interface MatchForMatchday {
  id: number;
  match_date: string;
  deadline?: string;
  status: string;
  matchday?: number;
  stage?: string;
  /** Set by the knockout-bracket injector on frontend-only placeholder rows. */
  isSynthetic?: boolean;
}

// Knockout-stage label mapping. The API-Football `stage` string is the source
// of truth and stays in EN; we only translate well-known round names for
// display. Unknown stages fall through to the raw string so the function
// remains generic for future cup formats.
const KNOCKOUT_STAGE_I18N_KEYS: Record<string, string> = {
  'Round of 128': 'tournament:roundOf128',
  'Round of 64': 'tournament:roundOf64',
  'Round of 32': 'tournament:roundOf32',
  'Round of 16': 'tournament:roundOf16',
  'Quarter-finals': 'tournament:quarterFinals',
  'Semi-finals': 'tournament:semiFinals',
  '3rd Place Final': 'tournament:thirdPlace',
  'Final': 'tournament:final',
};

// Get human-readable stage label from API-Football stage string
export const getStageLabel = (stage: string): string => {
  // API-Football stage format: "Regular Season - 3", "Group Stage - 2", "League Stage - 5", etc.
  const stageMatch = stage.match(/^(.+?)\s*-\s*(\d+)$/);
  if (stageMatch) {
    const stageName = stageMatch[1].trim();
    const num = parseInt(stageMatch[2], 10);
    if (stageName.toLowerCase().includes('regular season')) return i18n.t('tournament:round', { number: num });
    if (stageName.toLowerCase().includes('group')) return i18n.t('tournament:group', { number: num });
    return `${stageName} ${num}`;
  }

  // Knockout stages — translate the well-known round names. If the i18n key
  // doesn't resolve to anything (locale file missing the key for whatever
  // reason), `i18next` returns the key itself which would surface in the UI
  // as e.g. "tournament:final". Detect that and fall through to the raw stage
  // string so the user sees the English name from the API instead.
  const i18nKey = KNOCKOUT_STAGE_I18N_KEYS[stage];
  if (i18nKey) {
    const translated = i18n.t(i18nKey);
    if (translated && translated !== i18nKey) return translated;
  }

  // Standalone stage from API — already human-readable (Quarter-finals, Play-offs, etc.)
  return stage;
};

/**
 * Group key of a match: `${stage}-${matchday}` for numbered rounds, the bare
 * stage for standalone stages (knockouts). Equals `StageMatchday.id` of the
 * group the match belongs to.
 */
export function getStageMatchdayKey(match: Pick<MatchForMatchday, 'stage' | 'matchday'>): string {
  return match.matchday == null ? match.stage! : `${match.stage}-${match.matchday}`;
}

/**
 * Build stage-aware matchdays from matches.
 * For knockout stages, group by stage only (ignore matchday).
 * For league stages, group by stage + matchday.
 */
export function buildStageMatchdays<T extends MatchForMatchday>(matches: T[]): StageMatchday[] {
  const groupMap = new Map<string, T[]>();

  matches.forEach(match => {
    const key = getStageMatchdayKey(match);

    if (!groupMap.has(key)) {
      groupMap.set(key, []);
    }
    groupMap.get(key)!.push(match);
  });

  const result: StageMatchday[] = [];

  groupMap.forEach((groupMatches, key) => {
    const isStandalone = groupMatches[0].matchday == null;
    const lastDashIdx = key.lastIndexOf('-');
    const stage = isStandalone ? key : key.substring(0, lastDashIdx);
    const matchday = isStandalone ? 0 : parseInt(key.substring(lastDashIdx + 1), 10);
    const matchDates = groupMatches.map(m => new Date(m.match_date).getTime());
    // A stage is "synthetic" only when EVERY match in the group is a
    // frontend-injected placeholder. As soon as the API publishes one real
    // fixture for the stage, the mix-up causes this to fall back to undefined
    // and the stage rejoins the auto-detection pool.
    const isSynthetic = groupMatches.every(m => m.isSynthetic === true);

    result.push({
      id: key,
      stage,
      matchday,
      label: getStageLabel(stage),
      matches_count: groupMatches.length,
      finished_count: groupMatches.filter(m => isCompleted(m.status) || isDisrupted(m.status)).length,
      upcoming_count: groupMatches.filter(m => isPreMatch(m.status) || isLiveStatus(m.status)).length,
      earliest_date: matchDates.length > 0 ? new Date(Math.min(...matchDates)).toISOString() : '',
      latest_date: matchDates.length > 0 ? new Date(Math.max(...matchDates)).toISOString() : '',
      ...(isSynthetic ? { isSynthetic: true } : {}),
    });
  });

  return orderStageMatchdays(result);
}

const toTime = (iso: string): number => (iso ? new Date(iso).getTime() : 0);

/**
 * Order stages for display.
 *
 * Numbered rounds (matchday > 0) always sort by matchday — never by date,
 * because rescheduled fixtures routinely make a later round start before an
 * earlier one finishes and must not drag the round out of numeric order.
 *
 * Standalone stages (matchday === 0: qualifying rounds, play-offs, knockouts,
 * promotion/relegation playoffs) sort by their earliest match among
 * themselves and are placed relative to the numbered block as a whole:
 * a stage whose first match kicks off before the numbered block's first match
 * goes BEFORE the block (UEFA qualifying rounds), everything else goes AFTER
 * it (World Cup knockouts, domestic playoffs, cup rounds). Standalone stages
 * are never interleaved inside the numbered block, so a single rescheduled
 * league fixture cannot move a knockout stage into the middle of the season.
 */
function orderStageMatchdays(stages: StageMatchday[]): StageMatchday[] {
  const numbered = stages.filter(s => s.matchday > 0).sort((a, b) => a.matchday - b.matchday);
  const standalone = stages
    .filter(s => s.matchday === 0)
    .sort((a, b) => toTime(a.earliest_date) - toTime(b.earliest_date));

  if (numbered.length === 0) return standalone;

  const numberedTimes = numbered.map(s => toTime(s.earliest_date)).filter(t => t > 0);
  if (numberedTimes.length === 0) return [...numbered, ...standalone];

  const blockStart = Math.min(...numberedTimes);
  const before = standalone.filter(s => toTime(s.earliest_date) > 0 && toTime(s.earliest_date) < blockStart);
  const after = standalone.filter(s => !before.includes(s));

  return [...before, ...numbered, ...after];
}

/**
 * Build virtual Extra tabs for rescheduled matches.
 * Groups rescheduled matches by the chronological gap they fall into (between which two rounds).
 * Each group becomes a separate Extra tab inserted at the right position.
 */
export function buildExtraRound<T extends MatchForMatchday>(
  stageMatchdays: StageMatchday[],
  matches: T[],
): { stageMatchdaysWithExtra: StageMatchday[]; extraMatches: T[]; extraMatchGroups: Record<string, T[]> } {
  const leagueMatchdays = stageMatchdays.filter(sm => sm.matchday > 0);

  if (leagueMatchdays.length === 0) {
    return { stageMatchdaysWithExtra: stageMatchdays, extraMatches: [], extraMatchGroups: {} };
  }

  const sortedLeague = [...leagueMatchdays].sort((a, b) => a.matchday - b.matchday);
  const activeLeagueRound = sortedLeague.find(sm => sm.finished_count < sm.matches_count);
  const activeRoundNumber = activeLeagueRound?.matchday ?? sortedLeague[sortedLeague.length - 1].matchday + 1;

  // Compute activeRoundLatest from FINISHED matches only (excluding postponed outliers)
  const finishedActiveRoundDates = matches
    .filter(m => m.matchday === activeRoundNumber && (isCompleted(m.status) || isDisrupted(m.status)))
    .map(m => new Date(m.match_date).getTime());
  const activeRoundLatest = finishedActiveRoundDates.length > 0
    ? Math.max(...finishedActiveRoundDates)
    : activeLeagueRound?.latest_date
      ? new Date(activeLeagueRound.latest_date).getTime()
      : Date.now();

  const rescheduled = matches.filter(m => {
    if (m.matchday == null) return false;
    const matchday = m.matchday;
    const matchDate = new Date(m.match_date).getTime();

    // Case 1: Match from a future round playing significantly before its own round starts.
    // Only flag if the match's own round has an earliest_date and the match plays well before it.
    if (matchday > activeRoundNumber + 1 && matchDate <= activeRoundLatest) {
      const matchRoundInfo = sortedLeague.find(sm => sm.matchday === matchday);
      if (matchRoundInfo?.earliest_date) {
        const roundStart = new Date(matchRoundInfo.earliest_date).getTime();
        // If match is within 48h of its round's start, it's normal scheduling
        if (matchDate >= roundStart - 48 * 3600 * 1000) return false;
      }
      return true;
    }

    // Case 2: SCHEDULED match rescheduled to the future
    if (matchday <= activeRoundNumber && isPreMatch(m.status) && matchDate > activeRoundLatest) {
      // Check if match is still within its own round's date range — that's normal scheduling
      const roundInfo = sortedLeague.find(sm => sm.matchday === matchday);
      if (roundInfo?.latest_date) {
        const roundLatest = new Date(roundInfo.latest_date).getTime();
        // If match date is within the round's latest_date, it's the last game of the round — not rescheduled
        if (matchDate <= roundLatest) return false;
      }

      // Past rounds: always flag (any remaining SCHEDULED match is definitely postponed)
      if (matchday < activeRoundNumber) return true;
      // Active round: only flag if round is mostly done (avoid flagging normal weekend matches)
      if (roundInfo && roundInfo.finished_count > roundInfo.matches_count / 2) return true;
    }

    return false;
  });

  if (rescheduled.length === 0) {
    return { stageMatchdaysWithExtra: stageMatchdays, extraMatches: [], extraMatchGroups: {} };
  }

  // Group rescheduled matches by the round they chronologically fall after
  const groupsByAfterRound = new Map<number, T[]>();

  rescheduled.forEach(m => {
    const matchDate = new Date(m.match_date).getTime();
    let afterRound = 0;
    for (const sm of sortedLeague) {
      if (sm.latest_date && new Date(sm.latest_date).getTime() < matchDate) {
        afterRound = sm.matchday;
      }
    }
    if (!groupsByAfterRound.has(afterRound)) {
      groupsByAfterRound.set(afterRound, []);
    }
    groupsByAfterRound.get(afterRound)!.push(m);
  });

  // Create Extra tabs and insert each after its corresponding round
  const result = [...stageMatchdays];
  const extraMatchGroups: Record<string, T[]> = {};
  const sortedAfterRounds = [...groupsByAfterRound.keys()].sort((a, b) => b - a);

  for (const afterRound of sortedAfterRounds) {
    const groupMatches = groupsByAfterRound.get(afterRound)!;
    const dates = groupMatches.map(m => new Date(m.match_date).getTime());
    const extraId = `EXTRA_${afterRound}`;

    extraMatchGroups[extraId] = groupMatches;

    const extraTab: StageMatchday = {
      id: extraId,
      stage: 'EXTRA',
      matchday: 0,
      label: `\u26A1 ${i18n.t('tournament:extra')}`,
      matches_count: groupMatches.length,
      finished_count: groupMatches.filter(m => isCompleted(m.status)).length,
      upcoming_count: groupMatches.filter(m => isPreMatch(m.status) || isLiveStatus(m.status)).length,
      earliest_date: new Date(Math.min(...dates)).toISOString(),
      latest_date: new Date(Math.max(...dates)).toISOString(),
    };

    // afterRound === 0 means "before the first numbered round" — which may be
    // preceded by standalone qualifying stages, so anchor on the numbered
    // block rather than on index 0.
    let insertIndex = result.findIndex(s => s.matchday > 0);
    if (insertIndex === -1) insertIndex = 0;
    if (afterRound > 0) {
      for (let i = 0; i < result.length; i++) {
        if (result[i].matchday === afterRound) {
          insertIndex = i + 1;
          break;
        }
      }
    }
    result.splice(insertIndex, 0, extraTab);
  }

  return { stageMatchdaysWithExtra: result, extraMatches: rescheduled, extraMatchGroups };
}

/**
 * Detect which matchday should be auto-selected on page load.
 * Priority: live matches → Extra with urgent deadline → lowest incomplete round → last round.
 */
export function detectInitialMatchday<T extends MatchForMatchday>(
  stageMatchdays: StageMatchday[],
  extraMatches: T[],
): string | null {
  // Filter out frontend-injected synthetic stages (e.g. World Cup 2026
  // knockout-bracket placeholders). They have realistic future dates and
  // would otherwise out-bid a currently in-progress group stage in the
  // "closest standalone stage to now" branch. Once the API publishes real
  // fixtures, the stage's `isSynthetic` flag flips and it rejoins the pool.
  const realStages = stageMatchdays.filter(sm => !sm.isSynthetic);

  // Priority 1: Round with live matches.
  // Natural definition: the round has at least one finished match AND at
  // least one match still to come (upcoming or live). The previous third
  // clause `upcoming + finished > matches_count` was mathematically
  // unsatisfiable (sum can never exceed the total), so this branch never
  // matched and the function always fell through to lower priorities.
  const liveRound = realStages.find(sm =>
    sm.upcoming_count > 0 && sm.finished_count > 0,
  );
  if (liveRound) return liveRound.id;

  // Priority 2: Extra tab with matches with deadline within 24 hours
  const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;
  const now = Date.now();
  const extraTabs = realStages.filter(sm => sm.stage === 'EXTRA');
  for (const extraTab of extraTabs) {
    const hasUrgent = extraMatches.some(m => {
      const deadline = m.deadline ? new Date(m.deadline).getTime() : new Date(m.match_date).getTime();
      const matchDate = new Date(m.match_date).getTime();
      const extraEarliest = new Date(extraTab.earliest_date).getTime();
      const extraLatest = new Date(extraTab.latest_date).getTime();
      return matchDate >= extraEarliest && matchDate <= extraLatest &&
        deadline > now && deadline - now < TWENTY_FOUR_HOURS;
    });
    if (hasUrgent) return extraTab.id;
  }

  // Priority 3: Lowest round number with playable matches
  const isExtraStage = (sm: StageMatchday) => sm.stage === 'EXTRA';
  const matchdayStages = [...realStages]
    .filter(sm => !isExtraStage(sm) && sm.matchday > 0)
    .sort((a, b) => a.matchday - b.matchday);
  const standaloneStages = [...realStages]
    .filter(sm => !isExtraStage(sm) && sm.matchday === 0)
    .sort((a, b) => {
      const dateA = a.earliest_date ? new Date(a.earliest_date).getTime() : 0;
      const dateB = b.earliest_date ? new Date(b.earliest_date).getTime() : 0;
      return dateA - dateB;
    });

  // Try matchday-based stages first
  const incompleteRounds = matchdayStages.filter(sm => sm.finished_count < sm.matches_count);
  if (incompleteRounds.length > 0) {
    for (const round of incompleteRounds) {
      const laterRounds = matchdayStages.filter(sm => sm.matchday > round.matchday);
      const hasLaterRoundWithEarlierDates = laterRounds.some(lr =>
        lr.earliest_date && round.latest_date &&
        new Date(lr.earliest_date).getTime() < new Date(round.latest_date).getTime(),
      );
      if (!hasLaterRoundWithEarlierDates) return round.id;
      const remaining = round.matches_count - round.finished_count;
      if (remaining > round.matches_count / 2) return round.id;
    }
    return incompleteRounds[incompleteRounds.length - 1].id;
  }

  // Try standalone stages (cups/knockouts) — find the stage closest to NOW
  if (standaloneStages.length > 0) {
    const now = Date.now();

    // Find stages with upcoming matches whose dates are in the future
    const futureStages = standaloneStages.filter(sm =>
      sm.upcoming_count > 0 && sm.latest_date && new Date(sm.latest_date).getTime() > now,
    );

    if (futureStages.length > 0) {
      // Pick the stage whose earliest_date is closest to now
      futureStages.sort((a, b) => {
        const distA = Math.abs(new Date(a.earliest_date).getTime() - now);
        const distB = Math.abs(new Date(b.earliest_date).getTime() - now);
        return distA - distB;
      });
      return futureStages[0].id;
    }

    // All done — show last completed stage
    const lastCompleted = [...standaloneStages].filter(sm => sm.finished_count > 0);
    if (lastCompleted.length > 0) return lastCompleted[lastCompleted.length - 1].id;
  }

  // Fallback: last matchday stage, then last standalone stage
  const lastMatchday = matchdayStages[matchdayStages.length - 1];
  const lastStandalone = standaloneStages[standaloneStages.length - 1];
  return lastMatchday?.id ?? lastStandalone?.id ?? null;
}

/**
 * Pick the "active" match for in-round highlight + auto-scroll on mount.
 * Pure function — caller filters the input to the active round's matches
 * first so we never highlight a card from a different round.
 *
 * Priority:
 *   1. Any live match (IN_PLAY/PAUSED/EXTRA_TIME/PENALTY_SHOOTOUT) —
 *      earliest kickoff wins if multiple.
 *   2. Soonest upcoming pre-match (SCHEDULED/TIMED) with match_date > now.
 *   3. null — round is entirely finished or empty.
 */
export function detectActiveMatch<T extends MatchForMatchday>(
  matches: T[],
  now: Date = new Date(),
): T | null {
  const nowMs = now.getTime();

  // Helper: parse match_date once, reject NaN. Returning null short-circuits
  // any match with a malformed date so it can't pollute the sort.
  const parse = (m: T): number | null => {
    const t = new Date(m.match_date).getTime();
    return Number.isFinite(t) ? t : null;
  };

  // Priority 1: live matches, earliest kickoff first.
  const live = matches
    .map(m => ({ m, t: parse(m) }))
    .filter((x): x is { m: T; t: number } => x.t !== null && isLiveStatus(x.m.status))
    .sort((a, b) => a.t - b.t);
  if (live.length > 0) return live[0].m;

  // Priority 2: soonest upcoming pre-match.
  const upcoming = matches
    .map(m => ({ m, t: parse(m) }))
    .filter((x): x is { m: T; t: number } => x.t !== null && isPreMatch(x.m.status) && x.t > nowMs)
    .sort((a, b) => a.t - b.t);
  if (upcoming.length > 0) return upcoming[0].m;

  return null;
}
